import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { ShareButtons } from '@/components/ShareButtons';
import { Calendar, ArrowLeft, TrendingUp, ExternalLink } from 'lucide-react';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix double-encoded UTF-8 characters (e.g., â€" should be —)
const fixEncoding = (text) => {
  if (!text) return '';
  return text
    // Em-dash variations
    .replace(/â€"/g, '—')
    .replace(/â€"/g, '–')
    .replace(/â€"(?=[0-9])/g, '—')  // em-dash before numbers
    // Quote variations
    .replace(/â€™/g, "'")
    .replace(/â€˜/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/â€¦/g, '…')
    // Space and accent variations
    .replace(/Â /g, ' ')
    .replace(/Ã©/g, 'é')
    .replace(/Ã¨/g, 'è')
    .replace(/Ã /g, 'à')
    // Catch any remaining â€ patterns (generic cleanup)
    .replace(/â€\u009d/g, '"')
    .replace(/â€\u0093/g, '"')
    .replace(/â€/g, '—');  // Final fallback for em-dash
};

export default function PickDetail() {
  const { id } = useParams();
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPick = async () => {
      try {
        const response = await axios.get(`${API}/picks/${id}`);
        setPick(response.data);
      } catch (error) {
        console.error('Failed to fetch pick:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPick();
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getConfidenceColor = (level) => {
    if (level >= 8) return 'text-accent';
    if (level >= 5) return 'text-secondary';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen" data-testid="loading-indicator">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </Layout>
    );
  }

  if (!pick) {
    return (
      <Layout>
        <div className="container mx-auto px-6 md:px-12 py-12 text-center" data-testid="pick-not-found">
          <h1 className="text-3xl font-heading font-bold mb-4">Pick Not Found</h1>
          <Link to="/" className="text-primary hover:underline">Return to Home</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="py-12" data-testid="pick-detail">
        <div className="container mx-auto px-6 max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest hover:text-primary transition-colors mb-8" data-testid="back-link">
            <ArrowLeft size={16} />
            Back to Picks
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <span className="font-mono text-xs uppercase tracking-widest text-primary px-3 py-1 border border-primary" data-testid="pick-sport">{pick.sport}</span>
              <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground" data-testid="pick-date">
                <Calendar size={12} />
                {formatDate(pick.pick_date)}
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" data-testid="pick-title">{pick.player_name} - {pick.prop_type}</h1>
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp size={20} className={getConfidenceColor(pick.confidence_level)} />
              <span className={`font-mono text-lg font-bold ${getConfidenceColor(pick.confidence_level)}`} data-testid="pick-confidence">
                CONFIDENCE: {pick.confidence_level}/10
              </span>
            </div>
          </div>

          {/* Thumbnail Image Section */}
          {pick.thumbnail_url && (
            <div className="mb-12" data-testid="thumbnail-section">
              <div className="aspect-video max-h-[400px] bg-surface rounded-lg overflow-hidden">
                <img 
                  src={pick.thumbnail_url.startsWith('/') ? `${BACKEND_URL}${pick.thumbnail_url}` : pick.thumbnail_url} 
                  alt={`${pick.player_name} analysis`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-card border border-border p-6">
              <h3 className="font-heading text-lg font-bold mb-2">LINE</h3>
              <p className="font-mono text-2xl" data-testid="pick-line">{pick.line}</p>
            </div>
            <div className="bg-card border border-border p-6 md:col-span-2">
              <h3 className="font-heading text-lg font-bold mb-2">THE PICK</h3>
              <p className="font-mono text-2xl text-primary" data-testid="pick-prediction">{pick.pick}</p>
            </div>
          </div>

          <div className="space-y-8 mb-12">
            <div className="bg-surface-highlight border border-border p-8">
              <h2 className="font-heading text-2xl font-bold mb-4">REASONING</h2>
              <div className="pick-content" data-testid="pick-reasoning">
                {fixEncoding(pick.reasoning).split('\n').map((paragraph, index) => (
                  paragraph.trim() && <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </div>

            {pick.player_stats && (
              <div className="bg-surface-highlight border border-border p-8">
                <h2 className="font-heading text-2xl font-bold mb-4">PLAYER STATS</h2>
                <div className="pick-content font-mono text-sm" data-testid="pick-stats">
                  {fixEncoding(pick.player_stats).split('\n').map((line, index) => (
                    line.trim() && <p key={index} className="mb-2">{line}</p>
                  ))}
                </div>
              </div>
            )}

            {pick.matchup_analysis && (
              <div className="bg-surface-highlight border border-border p-8">
                <h2 className="font-heading text-2xl font-bold mb-4">MATCHUP ANALYSIS</h2>
                <div className="pick-content" data-testid="pick-matchup">
                  {pick.matchup_analysis.split('\n').map((paragraph, index) => (
                    paragraph.trim() && <p key={index} className="mb-4">{paragraph}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {pick.affiliate_links && pick.affiliate_links.length > 0 && (
            <div className="border-t border-border pt-8 mb-8" data-testid="affiliate-links">
              <h3 className="font-heading text-xl font-bold mb-4">RECOMMENDED RESOURCES</h3>
              <div className="space-y-3">
                {pick.affiliate_links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline font-mono"
                    data-testid={`affiliate-link-${index}`}
                  >
                    <ExternalLink size={16} />
                    {link.text}
                  </a>
                ))}
              </div>
            </div>
          )}

          <ShareButtons pick={pick} />
        </div>
      </article>
    </Layout>
  );
}